import {
  isCorpTenant,
  useIsAutoApprovalEnabled,
  useIsTravelApp,
  UserSourceProvider,
} from "@capone/common";
import { ExperimentsHookProvider } from "@capone/experiments";
import { Box } from "@material-ui/core";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core/styles";
import { AgentBanner, TravelerWorkflowDialog, useDeviceTypes } from "halifax";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { Provider, useDispatch, useSelector } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import {
  CallState,
  CoreStaysCheckout,
  CorpSessionInfo,
  CustomEvents,
  EditTravelerModalState,
  HotelsClientAssetProps,
  HotelsModuleProps,
  IPerson,
  ModalNames,
  SavedTravelersProperties,
  SelfServeEvents,
  SessionInfo,
  VIEWED_TRAVELER_DETAILS,
  ViewedSavedTravelersProperties,
  ViewedTravelerDetailsProperties,
} from "redmond";
import { enableMapSet } from "immer";

import { fetchUserPolicies } from "b2b-base/src/api/v1/policies/fetchPolicies";
import { useMedalliaFeedback } from "b2b-base/src/components/MedalliaFeedback";
import { useSeenModalUpdater } from "b2b-base/src/hooks/useSeenModalUpdater";
import { config } from "./api/config";
import { trackEvent } from "./api/v0/analytics/trackEvent";
import fetchUserInfo from "./api/v0/user/fetchUserInfo";
import AxiosInterceptors from "./components/AxiosInterceptors";
import ExperimentsProvider, {
  FINTECH_HOTEL_UX_UPDATED,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  getExperimentVariantJSON,
  HOTEL_CFAR_MODEL_V1,
  HOTEL_CFAR_REFUND_DISPLAY,
  HOTEL_CFAR_REFUND_DISPLAY_VARIANTS,
  HOTELS_CFAR,
  HOTELS_CFAR_ELIGIBLE_POLICY,
  HOTELS_CFAR_ELIGIBLE_POLICY_VARIANTS,
  HBUX_LODGING_ERROR_COPY_UPDATE,
  useExperiments,
} from "./context/experiments";
import useWindowEventListener from "./hooks/useWindowEventListener";
import * as englishTranslations from "./lang/en.json";
import * as frenchTranslations from "./lang/fr.json";
import { HotelAncillary } from "./modules/ancillary";
import { setAncillaryExperiments } from "./modules/ancillary/actions/actions";
import { HotelBook } from "./modules/book";
import {
  deleteUserPassenger,
  fetchCorpUserPassengers,
  fetchUserPassengers,
  updateUserPassenger,
} from "./modules/book/actions/actions";
import { PriceMatchBanner } from "./modules/book/components/PriceMatchBanner";
import { getUserPassengers } from "./modules/book/reducer";
import {
  setAgentEmail,
  setCorporateInfo,
  setIsAutoApprovalEnabled,
  setIsFirstLaunch,
  setSelectedAccountReferenceId,
  setUserInfo,
} from "./modules/rewards/actions/actions";
import { RewardsBanner } from "./modules/rewards/components";
import { getRewardsAccounts } from "./modules/rewards/reducer";
import { store } from "./store";
import { eventsToListen } from "./utils/events";
import { PATH_ANCILLARY, PATH_BOOK } from "./utils/paths";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}

const generateClassName = createGenerateClassName({
  productionPrefix: "ptHotelsModule",
  seed: "ptHotelsModule",
});

interface HotelsClientContextType extends HotelsClientAssetProps {
  isAgentPortal: boolean;
  isAutoApprovalEnabled: boolean;
}

export const ClientContext = React.createContext<
  Partial<HotelsClientContextType>
>({});

const App = (props: HotelsModuleProps) => {
  const {
    baseHistory,
    clientAssets,
    experiments,
    isAgentPortal,
    language,
    theme,
    core,
  } = props;

  const [activeTheme, setActiveTheme] = useState(theme);
  const [locale, setLocale] = useState(language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);
  const [policies, setPolicies] = useState(clientAssets.policies);

  useEffect(() => {
    handleFetchUserInfo();
  }, []);

  useEffect(() => {
    if (!policies && isCorpTenant(config.TENANT)) {
      handleFetchPolicies();
    }
  }, [policies]);

  enableMapSet();

  const handleFetchUserInfo = async () => {
    try {
      const userInfoResponse = await fetchUserInfo();
      setSessionInfo(userInfoResponse);
    } catch (error) {
      console.error(error);
    }
  };

  const handleFetchPolicies = async () => {
    const policiesResponse = await fetchUserPolicies();
    setPolicies(policiesResponse.policyDetail);
  };

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const localMessages = loadLocaleData(e.detail);
    setMessages(localMessages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged
  );
  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  const isAutoApprovalEnabled = useIsAutoApprovalEnabled(
    clientAssets.policies || policies
  );

  const { isTravelApp } = useIsTravelApp();
  const { isRewardsDisabled } = clientAssets;

  return (
    <Provider store={store}>
      <ExperimentsProvider
        initState={{
          experiments: experiments?.experiments || [],
          trackingProperties: experiments?.trackingProperties,
          callState: CallState.NotCalled,
        }}
      >
        <ExperimentsHookProvider isLoggedIn={Boolean(sessionInfo?.csrfToken)}>
          <UserSourceProvider>
            <Router history={baseHistory}>
              <AxiosInterceptors isAgentPortal={isAgentPortal} />
              <ClientContext.Provider
                value={{
                  ...clientAssets,
                  sessionInfo,
                  policies,
                  isAgentPortal,
                  isAutoApprovalEnabled,
                }}
              >
                <div className="App">
                  <StylesProvider
                    generateClassName={generateClassName}
                    injectFirst
                  >
                    <ThemeProvider theme={activeTheme}>
                      {messages != null ? (
                        <IntlProvider
                          locale={locale}
                          defaultLocale="en"
                          messages={messages}
                        >
                          <Switch>
                            {isAgentPortal ? (
                              <Route path="*">
                                <AgentBanner
                                  firstName={firstName}
                                  lastName={lastName}
                                />
                              </Route>
                            ) : (
                              <>
                                <Route path={PATH_BOOK}>
                                  {!isTravelApp &&
                                    !isRewardsDisabled && <PriceMatchBanner />}
                                </Route>
                                <Route path="*">
                                  {!isTravelApp && <RewardsBanner />}
                                </Route>
                              </>
                            )}
                          </Switch>
                          <Body
                            sessionInfo={sessionInfo}
                            setSessionInfo={setSessionInfo}
                            clientAssets={{ ...clientAssets, policies }}
                            core={core}
                          />
                        </IntlProvider>
                      ) : (
                        <div>Loading</div>
                      )}
                    </ThemeProvider>
                  </StylesProvider>
                </div>
              </ClientContext.Provider>
            </Router>
          </UserSourceProvider>
        </ExperimentsHookProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

export const Body = (props: {
  sessionInfo?: SessionInfo | CorpSessionInfo;
  setSessionInfo: React.Dispatch<
    React.SetStateAction<SessionInfo | CorpSessionInfo | undefined>
  >;
  clientAssets: HotelsClientAssetProps;
  core?: CoreStaysCheckout;
}) => {
  const isCorp = isCorpTenant(config.TENANT);

  const { sessionInfo, setSessionInfo, core } = props;
  const dispatch = useDispatch();
  const expState = useExperiments();
  const { matchesMobile } = useDeviceTypes();
  const travelers = useSelector(getUserPassengers);

  const rewardsAccounts = useSelector(getRewardsAccounts, (a, b) => {
    if (a.length !== b.length) {
      return false;
    }
    return true;
  });

  const [editTravelersOpen, setEditTravelersOpen] = useState(
    EditTravelerModalState.closed
  );

  const closeEditTravelersModal = useCallback(() => {
    setEditTravelersOpen(EditTravelerModalState.closed);
  }, []);

  const onEditTravelersStateChange = useCallback((ev: any) => {
    const newEditModalState =
      (ev as CustomEvent).detail ?? EditTravelerModalState.closed;

    setEditTravelersOpen(newEditModalState);
  }, []);

  const userRoles =
    sessionInfo && "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo.role
      : [];

  const defaultTravelerId =
    sessionInfo && "corporateInfo" in sessionInfo
      ? sessionInfo.corporateInfo.defaultTravelerId
      : "";

  const hasSeenNfuInfoModal =
    (sessionInfo as CorpSessionInfo).corporateInfo?.hasSeenModalMap?.[
      ModalNames.NFU_IN_PRODUCT_AWARENESS_PROFILES
    ] ?? false;

  useEffect(() => {
    if (editTravelersOpen === EditTravelerModalState.hotel) {
      if (isCorp) {
        trackEvent({
          eventName: VIEWED_TRAVELER_DETAILS,
          properties: {
            num_traveler_profiles: travelers.length,
          } satisfies ViewedTravelerDetailsProperties,
        });
      } else {
        trackEvent({
          eventName: SelfServeEvents.ViewedSavedTravelers,
          properties: {
            url: window.location.pathname,
            num_traveler_profiles: travelers.length,
          } satisfies ViewedSavedTravelersProperties,
        });
      }
    }
  }, [editTravelersOpen, travelers.length]);

  const handleAddPass = useCallback(() => {
    !isCorp &&
      trackEvent({
        eventName: SelfServeEvents.ClickSavedTravelersAddNew,
        properties: {
          url: window.location.pathname,
        },
      });
  }, []);

  useEffect(() => {
    if (!isCorp) {
      dispatch(fetchUserPassengers());
    }

    document.addEventListener(
      CustomEvents.editTravelersStateChange,
      onEditTravelersStateChange
    );

    return () => {
      document.removeEventListener(
        CustomEvents.editTravelersStateChange,
        onEditTravelersStateChange
      );
    };
  }, []);

  useEffect(() => {
    if (isCorp && rewardsAccounts.length > 0) {
      dispatch(fetchCorpUserPassengers());
    }
  }, [rewardsAccounts.length]);

  const handleAccountSelected = (event: CustomEvent) => {
    dispatch(setSelectedAccountReferenceId(event.detail));
  };

  useWindowEventListener(
    "mclean_rewards_account_selected",
    handleAccountSelected
  );

  useEffect(() => {
    if (sessionInfo) {
      if ("corporateInfo" in sessionInfo) {
        dispatch(setCorporateInfo(sessionInfo.corporateInfo));
      }
      dispatch(setUserInfo(sessionInfo.userInfo));
      dispatch(setIsFirstLaunch(sessionInfo.isFirstSession));
      if (sessionInfo.isDelegatedSession) {
        dispatch(setAgentEmail(sessionInfo.isDelegatedSession));
      }
    }
  }, [sessionInfo, dispatch]);

  useEffect(() => {
    dispatch(
      setAncillaryExperiments({
        [HOTELS_CFAR]: getExperimentVariant(expState.experiments, HOTELS_CFAR),
        [HOTEL_CFAR_REFUND_DISPLAY]: getExperimentVariantCustomVariants(
          expState.experiments,
          HOTEL_CFAR_REFUND_DISPLAY,
          HOTEL_CFAR_REFUND_DISPLAY_VARIANTS
        ),
        [HOTELS_CFAR_ELIGIBLE_POLICY]: getExperimentVariantCustomVariants(
          expState.experiments,
          HOTELS_CFAR_ELIGIBLE_POLICY,
          HOTELS_CFAR_ELIGIBLE_POLICY_VARIANTS
        ),
        [FINTECH_HOTEL_UX_UPDATED]: getExperimentVariant(
          expState.experiments,
          FINTECH_HOTEL_UX_UPDATED
        ),
        [HOTEL_CFAR_MODEL_V1]: getExperimentVariantJSON(
          expState.experiments,
          HOTEL_CFAR_MODEL_V1
        ),
        [HBUX_LODGING_ERROR_COPY_UPDATE]: getExperimentVariant(
          expState.experiments,
          HBUX_LODGING_ERROR_COPY_UPDATE
        ),
      })
    );
  }, [expState]);

  const { isAutoApprovalEnabled } = useContext(ClientContext);

  useEffect(() => {
    dispatch(setIsAutoApprovalEnabled(Boolean(isAutoApprovalEnabled)));
  }, [dispatch, isAutoApprovalEnabled]);

  useMedalliaFeedback();

  return (
    <>
      <Box className="main-section">
        <Route path={PATH_ANCILLARY}>
          <HotelAncillary />
        </Route>
        <Route path={PATH_BOOK}>
          <HotelBook core={core} />
        </Route>
      </Box>
      <TravelerWorkflowDialog
        tenant={config.TENANT}
        showHotelLoyaltySection
        showGroundLoyaltySection
        disableDriversLicenseValidation
        disallowSelect
        displayModalSubtitle
        showAdditionalInfoSection
        showDriverLicenseSection
        showFrequentFlyerSection
        showGenderField
        showNationalityField
        buttonClassName="b2b"
        dialogProps={{
          className: "hotel-edit-traveler",
          onBackdropClick: closeEditTravelersModal,
          onClose: closeEditTravelersModal,
          open: editTravelersOpen === EditTravelerModalState.hotel,
        }}
        errorMessage="Add or choose one traveler to continue."
        fullScreenWithBanner={matchesMobile}
        handleDeletePassenger={(personId: string) => {
          dispatch(deleteUserPassenger({ personId }));
        }}
        handleUpdatePassenger={(
          person: IPerson,
          hideProfileAction,
          entryPoint
        ) => {
          dispatch(
            updateUserPassenger(
              { person, hideProfileAction },
              true,
              undefined,
              defaultTravelerId,
              entryPoint
            )
          );

          const frequentFlyerProgramsTotal = Reflect.ownKeys(
            person.frequentFlyer
          ).length;

          !isCorp &&
            trackEvent({
              eventName: SelfServeEvents.ClickSavedTravelersSaveTraveler,
              properties: {
                url: window.location.pathname,
                frequent_flyer_program_added: frequentFlyerProgramsTotal > 0,
                num_frequent_flyer_programs: frequentFlyerProgramsTotal,
              } satisfies SavedTravelersProperties,
            });
        }}
        onAddPass={handleAddPass}
        isMobile={matchesMobile}
        travelers={travelers}
        titles={{}}
        dialogContentProps={{}}
        userRoles={userRoles}
        trackEvent={trackEvent}
        defaultTravelerId={defaultTravelerId}
        hasSeenNfuInfo={hasSeenNfuInfoModal}
        handleSeenNfuInfoModal={useSeenModalUpdater(
          sessionInfo,
          setSessionInfo
        )}
      />
    </>
  );
};

export default App;
